import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import SettingCard from '../../settings/SettingCard';
import * as styles from './InvitationSettings.module.scss';

const SETTING_FRAGMENT = gql(`
  fragment InvitationSettings_settings on OrganizationSettings {
    requireInviteApproval
  }
`);

const UPDATE_INVITATION_SETTINGS_MUTATION = gql(/** @complexity 4 */ `
  mutation updateInvitationSettings($requireInviteApproval: Boolean!) {
    organization: updateOrganization(settings: { requireInviteApproval: $requireInviteApproval }) {
      id
      settings {
        ...InvitationSettings_settings
      }
    }
  }
`);

const OPTIONS = {
  enabled: 'Invitations must be approved by an admin',
  disabled: 'Users can directly invite new teammates',
} as const;

type InviteApprovalValue = keyof typeof OPTIONS;

interface InvitationSettingsProps {
  settings: DocumentType<typeof SETTING_FRAGMENT>;
}

const InvitationSettings = ({ settings }: InvitationSettingsProps) => {
  const [isEditing, setIsEditing] = useState(false);

  const [updateInvitationSettings] = useMutation(
    UPDATE_INVITATION_SETTINGS_MUTATION,
  );

  const currentValue = settings.requireInviteApproval ? 'enabled' : 'disabled';

  const handleSubmit = async ({
    requireInviteApproval,
  }: {
    requireInviteApproval: InviteApprovalValue;
  }) => {
    await updateInvitationSettings({
      variables: {
        requireInviteApproval: requireInviteApproval === 'enabled',
      },
    });
    setIsEditing(false);
  };

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Invitations"
          description={
            <Form<{ requireInviteApproval: InviteApprovalValue }>
              initialValues={{ requireInviteApproval: currentValue }}
              onSubmit={handleSubmit}
            >
              <div className={styles.formFields}>
                {(['disabled', 'enabled'] as const).map((value) => (
                  <Field
                    key={value}
                    label={OPTIONS[value]}
                    name="requireInviteApproval"
                    type="radio"
                    value={value}
                  />
                ))}
              </div>
              <div className={styles.formFooter}>
                <Submit variant="primary">Save</Submit>
              </div>
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            <Button onClick={() => setIsEditing(true)} variant="secondary">
              Edit
            </Button>
          }
          title="Invitations"
          description={OPTIONS[currentValue]}
        />
      )}
    </>
  );
};

export default InvitationSettings;
